<template>
  <div>
    <!-- Header -->
    <div class="d-flex justify-content-between">
      <div v-if="scheduleData.details">
        <div class="d-flex">
          <h3 class="mb-0">
            {{ scheduleData.details.name }}
          </h3>
          <b-badge
            v-if="scheduleData.details.deactivatedAt"
            class="ml-1"
            variant="danger"
          >
            {{ $t('Deactivated') }}
          </b-badge>
        </div>

        <p class="mb-0">
          {{ scheduleData.details.description }}
        </p>
      </div>

      <div v-if="!preview">
        <b-button
          v-b-tooltip.hover="$t('Update Schedule')"
          class="edit-btn ml-1"
          @click="$emit('update')"
        >
          <feather-icon icon="Edit2Icon" />
        </b-button>

        <b-button
          v-b-tooltip.hover
          :title="scheduleData.details.deactivatedAt ? $t('Activate Schedule') : $t('Deactivate Schedule')"
          class="edit-btn ml-1"
          :variant="scheduleData.details.deactivatedAt ? 'success' : 'danger'"
          @click="() => $bvModal.show(`change-schedule-status-${scheduleData.details.uuid}-modal`)"
        >
          <feather-icon :icon="scheduleData.details.deactivatedAt ? 'ClockIcon' : 'Trash2Icon'" />
        </b-button>
        <hr>
      </div>
    </div>

    <!-- Main Content -->
    <b-row class="schedule-summary justify-content-between">
      <!-- Source -->
      <b-col
        cols="5"
        class="pr-0"
      >
        <h5>{{ scheduleData.sourceShare?.label }}</h5>
        <b-row>
          <b-col
            cols="3"
            md="2"
            class="px-0"
          >
            <b-avatar
              :size="preview && !IS_MOBILE() ? 48 : 38"
              class="mr-1"
            >
              <i
                :class="getWalletIcon(scheduleData.sourceShare.type)"
                :style="`font-size: ${preview && !IS_MOBILE() ? '25px' : '16px'}`"
              />
            </b-avatar>
          </b-col>
          <b-col
            cols="9"
            md="10"
            class="px-0 pl-md-1"
          >
            <p class="mb-0">
              {{ $t('Balance') }}: {{ formatAmount(scheduleData.sourceShare.balance) }}
            </p>
            <p class="mb-0">
              {{ $t('Family') }}: {{ scheduleData.sourceWallet?.label }}
            </p>
          </b-col>
        </b-row>
      </b-col>

      <b-col
        cols="1"
        md="2"
        class="px-0"
      >
        <i class="fa fa-arrow-right mb-1 mt-2 f-25" />
      </b-col>

      <!-- Destination -->
      <b-col
        cols="5"
        class="pl-0"
      >
        <h5>
          {{ scheduleData.destinationShare && scheduleData.destinationShare.id ? scheduleData.destinationShare.label : $t('Entire Family') }}
        </h5>
        <b-row class="align-items-center">
          <b-col
            cols="3"
            md="2"
            class="px-0"
          >
            <b-avatar
              :size="preview && !IS_MOBILE() ? 48 : 38"
              class="mr-1"
            >
              <i
                :class="getWalletIcon(scheduleData.destinationShare.type)"
                :style="`font-size: ${preview && !IS_MOBILE() ? '25px' : '16px'}`"
              />
            </b-avatar>
          </b-col>
          <b-col
            cols="9"
            md="10"
            class="px-0 pl-md-1"
          >
            <p class="mb-0">
              {{ $t('Family') }}: {{ scheduleData.destinationWallet?.label }}
            </p>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <b-alert
      :show="true"
      class="p-1 mt-2 small d-flex"
      variant="warning"
    >
      <feather-icon
        icon="InfoIcon"
        size="48"
        class="mr-1"
      />
      <p>
        Every {{ paymentConfiguration.toLowerCase() }},
        {{ scheduleData.transferSetting.transferType === 'PERCENT' ? `${scheduleData.transferSetting.transferAmount}% of total balance of` : `${currency}${scheduleData.transferSetting.transferAmount} from` }}
        {{ scheduleData.sourceShare?.label }} will be transferred to
        {{ scheduleData.destinationShare?.id ? scheduleData.destinationShare?.label : `the entire family - ${scheduleData.destinationWallet?.label}` }}.
      </p>
    </b-alert>

    <!-- Deactivate Transfer Confirm Modal -->
    <confirm-modal
      v-if="!preview && scheduleData"
      :modal="`change-schedule-status-${scheduleData.details.uuid}-modal`"
      :title="scheduleData.details.deactivatedAt ? $t('Activate Scheduled Transfer') : $t('Deactivate Scheduled Transfer')"
      :description="`Are you sure you want to ${scheduleData.details.deactivatedAt ? 'activate' : 'deactivate'} the scheduled transfer - ${scheduleData.details.name}?`"
      :ok-variant="scheduleData.details.deactivatedAt ? 'primary' : 'danger'"
      :ok-title="scheduleData.details.deactivatedAt ? $t('Activate') : $t('Deactivate')"
      @confirm="changeScheduleStatus"
    />
  </div>
</template>

<script>
import {
  BAvatar, BAlert, BButton, BBadge, BRow, BCol,
} from 'bootstrap-vue'
import { computed, getCurrentInstance, ref } from 'vue'
import useApollo from '@/plugins/graphql/useApollo'
import useWallet from '@/utils/wallet'
import ConfirmModal from '../../reusables/ConfirmModal.vue'

export default {
  components: {
    BRow,
    BCol,
    BAvatar,
    BAlert,
    BButton,
    BBadge,
    ConfirmModal,
  },
  props: {
    scheduleData: {
      type: Object,
      required: true,
    },
    preview: {
      type: Boolean,
      default: () => false,
    },
    userType: {
      type: String,
      default: () => 'users',
    },
  },
  setup(props) {
    const root = getCurrentInstance().proxy.$root
    const store = root.$store

    const company = store.state.project.selectedCompany
    const currency = ref(store.state.project.company.currency)
    const { getWalletIcon } = useWallet()

    // Computed Properties
    const paymentConfiguration = computed(() => {
      const conf = props.scheduleData.schedule.configuration
      switch (props.scheduleData.schedule.type) {
      case 'WEEK':
        if (typeof conf === 'object') return `${conf.weekNum} ${conf.weekDay} OF THE MONTH`
        return `${(props.preview ? `${conf.split('_').join(' ')}` : `${conf.weekNum} ${conf.weekDay}`)} OF THE MONTH`
      case 'MONTHLY':
        return conf === 'FIRST_OF_MONTH' ? 'FIRST DAY OF THE MONTH' : 'LAST DAY OF THE MONTH'
      case 'DAYS':
        if (typeof conf === 'object') return `${conf.numberDays} DAYS`
        return `${conf.split('_')[0]} DAYS`
      case 'DATE':
        if (typeof conf === 'object') return `${root.ORDINAL_NUMBER(conf.day)} DAY OF THE MONTH`
        return `${root.ORDINAL_NUMBER(conf.split('_')[0])} DAY OF THE MONTH`
      default:
        return null
      }
    })

    // Methods
    const changeScheduleStatus = () => {
      const params = {
        scheduleUid: props.scheduleData.details.uuid,
        /* eslint-disable no-unneeded-ternary */
        activate: props.scheduleData.details.deactivatedAt ? true : false,
      }
      let api = 'activateWalletTransferSchedule'
      if (props.userType === 'company') {
        api = 'activateCompanyWalletTransferSchedule'
        params.companyUid = company
      }

      useApollo[props.userType][api](params).then(response => {
        root.showSuccessMessage({
          data: {
            message: response.data.activateWalletTransferSchedule.message,
          },
        })
      }).catch(error => {
        root.showErrorMessage(error)
      }).finally(() => {
        root.$emit('schedule-status-changed')
      })
    }

    return {
      currency,
      paymentConfiguration,
      changeScheduleStatus,
      getWalletIcon,
    }
  },
}
</script>

<style lang="scss" scoped>
@import "@core/scss/base/pages/wallet-page.scss";
</style>
